import { useState } from 'react';

export interface ClientPaginationProps {
  current: number;
  total: number;
  pageSize: number;
  onChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

/** Pages a list that is already fully loaded; spread `paginationProps` on <Pagination>. */
export function useClientPagination<T>(items: T[], initialPageSize: number) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  // Clamp the page during render instead of syncing via an effect: when
  // filtering or a delete shrinks the list below the current page, fall back
  // to the last page that exists.
  const lastPage = Math.max(1, Math.ceil(items.length / pageSize));
  const current = Math.min(page, lastPage);

  const paginationProps: ClientPaginationProps = {
    current,
    total: items.length,
    pageSize,
    onChange: setPage,
    onPageSizeChange: (size) => {
      setPageSize(size);
      setPage(1);
    },
  };

  return {
    pageItems: items.slice((current - 1) * pageSize, current * pageSize),
    paginationProps,
    resetPage: () => setPage(1),
  };
}
